import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button
} from '@mui/material';
import {
  TrackChanges as TrackChangesIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { SmsTriggerStatus } from '@/types/sms';

interface SmsStatusTrackerProps {
  status: SmsTriggerStatus | null;
  statusLoading: boolean;
  statusError: string | null;
  triggerResponse: any;
  onRefresh?: () => void;
}

export function SmsStatusTracker({
  status,
  statusLoading,
  statusError,
  triggerResponse,
  onRefresh
}: SmsStatusTrackerProps) {
  if (statusLoading && !status) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography variant="h6">Loading Status...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (statusError) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {statusError}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!status && !triggerResponse) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'failed':
        return <ErrorIcon />;
      case 'processing':
        return <ScheduleIcon />;
      case 'pending':
        return <SendIcon />;
      default:
        return <TrackChangesIcon />;
    }
  };

  const getProgressPercentage = () => {
    if (!status || status.total_count === 0) return 0;
    const processed = status.sent_count + status.failed_count;
    return Math.round((processed / status.total_count) * 100);
  };

  const isCompleted = status?.status === 'completed';
  const isFailed = status?.status === 'failed';
  const isProcessing = status?.status === 'processing' || status?.status === 'pending';

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <TrackChangesIcon color="primary" />
          <Typography variant="h6">SMS Processing Status</Typography>
          {status && (
            <Chip
              icon={getStatusIcon(status.status)}
              label={status.status.charAt(0).toUpperCase() + status.status.slice(1)}
              color={getStatusColor(status.status) as any}
              size="small"
            />
          )}
        </Box>

        {/* Keep the UI simple and non-technical – no IDs or internal details */}

        {status && (
          <>
            {/* Progress Bar */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getProgressPercentage()}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getProgressPercentage()}
                color={isCompleted ? 'success' : isFailed ? 'error' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {/* Status Grid */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {status.total_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {status.sent_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sent
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="error.main">
                    {status.failed_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {status.pending_count + status.queued_count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Status Messages */}
            {isProcessing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  SMS processing is in progress. This may take a few minutes for large batches.
                </Typography>
              </Alert>
            )}

            {isCompleted && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  SMS processing completed. {status.sent_count} SMS sent, {status.failed_count} failed.
                </Typography>
              </Alert>
            )}

            {isFailed && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  SMS processing failed. {status.error || 'Please try again or contact support.'}
                </Typography>
              </Alert>
            )}

          </>
        )}

        {statusLoading && status && (
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Updating status...
            </Typography>
          </Box>
        )}

        {/* Refresh Button */}
        {status && onRefresh && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                disabled={statusLoading}
              >
                Refresh Status
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

